import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAlertEvents } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Badge } from 'src/components/ui/Badge';
import { Skeleton } from 'src/components/ui/Skeleton';
import { ALERT_SURFACE_LABEL, CROSSING_LABEL } from 'src/lib/alerts';
import { readAlertsSeenAt } from 'src/lib/alertsSeen';
import { dayLabel, formatHourMinute, mexicoCityDayKey } from 'src/lib/time';
import type { EditorialAlertEvent } from 'src/types/editorial';

/**
 * The full record, grouped by day. `/alertas` only ever shows a handful of the most recent
 * matches — this is where "what happened last Tuesday" actually gets answered.
 */
export function AlertHistoryWorkspace() {
  const events = useAlertEvents({ rowsPerPage: 200 });
  const [search, setSearch] = useState('');
  const [seenAt] = useState(readAlertsSeenAt);
  const now = useState(() => new Date())[0];

  const filtered = filterEvents(events.data?.events ?? [], search);
  const groups = groupByDay(filtered, now);

  return (
    <div className="page-wide">
      <Link className="page-back" to="/alertas">
        ← Alertas
      </Link>

      <header className="landmark alerts__head">
        <div>
          <p className="thread__eyebrow">Centro de monitoreo</p>
          <h1 className="section-title">Historial de alertas</h1>
          <p className="relevance__note">Consulta todos los disparos registrados y entra a cada hallazgo para darle seguimiento.</p>
        </div>
      </header>

      {events.data && events.data.events.length > 0 && (
        <div className="alerts__search">
          <label>
            <span className="visually-hidden">Buscar en el historial</span>
            <span className="alerts__search-field">
              <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar actividad" />
            </span>
          </label>
        </div>
      )}

      {events.isPending && <Skeleton variant="rows" count={6} />}
      {events.isError && <LoadFailure title="No se pudo cargar el historial" error={events.error} onRetry={events.refetch} />}
      {events.data && events.data.events.length === 0 && <AbsenceState kind="empty" what="Ninguna regla se ha cumplido todavía." />}
      {events.data && events.data.events.length > 0 && filtered.length === 0 && (
        <AbsenceState kind="empty" what={`Ninguna coincidencia con “${search}”.`} />
      )}

      {groups.length > 0 && (
        <section className="timeline">
          {groups.map(group => (
            <div key={group.key}>
              <div className="timeline__day">{group.label.toUpperCase()}</div>
              {group.events.map(event => (
                <HistoryRow key={event.eventId} event={event} unseen={event.occurredAt !== null && Date.parse(event.occurredAt) > seenAt} />
              ))}
            </div>
          ))}
        </section>
      )}

      {events.data && (
        <p className="relevance__note mono">
          {filtered.length} de {events.data.totalRecords}
        </p>
      )}
    </div>
  );
}

function HistoryRow({ event, unseen }: { event: EditorialAlertEvent; unseen: boolean }) {
  // A feed alert has no broadcast to open: the artifact is the publisher's page, and the copy we
  // keep of it is a pointer there. Everything else opens the part of the broadcast that matched.
  const isFeedAlert = event.surface === 'feed_item';
  const openTo = event.chunkId
    ? `/stream/${encodeURIComponent(event.streamId)}/transcript`
    : event.storyId
      ? `/stream/${encodeURIComponent(event.streamId)}/stories`
      : `/stream/${encodeURIComponent(event.streamId)}/alertas`;

  return (
    <article className="event">
      <time>{formatHourMinute(event.occurredAt)}</time>
      <i className={`event__dot${unseen ? ' event__dot--unseen' : ''}`} aria-hidden="true" />
      <div>
        <h3>{event.ruleName || event.ruleId}</h3>
        <p>{event.excerpt || (event.crossing && CROSSING_LABEL[event.crossing]) || 'Sin detalle adicional.'}</p>
        <div className="event__meta">
          <span className="event__channel">
            {ALERT_SURFACE_LABEL[event.surface]} · {event.channelName || event.streamId}
          </span>
          <Badge tone={unseen ? 'contradicted' : 'supported'}>{unseen ? 'REVISAR' : 'VISTO'}</Badge>
        </div>
      </div>
      {isFeedAlert ? (
        <a className="event__open" href={event.feedLink} target="_blank" rel="noreferrer noopener">
          Abrir
        </a>
      ) : (
        <Link className="event__open" to={openTo}>
          Abrir
        </Link>
      )}
    </article>
  );
}

function filterEvents(events: Array<EditorialAlertEvent>, search: string): Array<EditorialAlertEvent> {
  const query = search.trim().toLowerCase();
  if (query === '') return events;
  return events.filter(
    event =>
      event.ruleName.toLowerCase().includes(query) ||
      event.excerpt.toLowerCase().includes(query) ||
      event.matchedKeywords.some(keyword => keyword.toLowerCase().includes(query)),
  );
}

type DayGroup = { key: string; label: string; events: Array<EditorialAlertEvent> };

/** Events already arrive newest-first from the API; grouping preserves that order within and
 *  across days rather than re-sorting. */
function groupByDay(events: Array<EditorialAlertEvent>, now: Date): Array<DayGroup> {
  const groups: Array<DayGroup> = [];

  for (const event of events) {
    const key = mexicoCityDayKey(event.occurredAt);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.events.push(event);
    } else {
      groups.push({ key, label: dayLabel(event.occurredAt, now), events: [event] });
    }
  }

  return groups;
}
